import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { readStorage, writeStorage } from '@/shared/lib/storage';

import { savePreferences } from './api/preferences';

export type Density = 'default' | 'compact';

interface Preferences {
  density: Density;
  setDensity: (d: Density) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
}

const Ctx = createContext<Preferences | null>(null);

/** User display preferences: kept locally for instant start-up and synced to the server. */
export function PreferencesProvider({ children, initial }: { children: ReactNode; initial?: { density?: Density | undefined; sidebarCollapsed?: boolean | undefined } }) {
  const [density, setDensityState] = useState<Density>(() => readStorage<Density>('density', initial?.density ?? 'default'));
  const [sidebarCollapsed, setCollapsedState] = useState<boolean>(() => readStorage<boolean>('sidebarCollapsed', initial?.sidebarCollapsed ?? false));

  useEffect(() => {
    document.documentElement.dataset['density'] = density;
  }, [density]);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    writeStorage('density', d);
    void savePreferences({ density: d }).catch(() => undefined);
  }, []);
  const setSidebarCollapsed = useCallback((v: boolean) => {
    setCollapsedState(v);
    writeStorage('sidebarCollapsed', v);
    void savePreferences({ sidebarCollapsed: v }).catch(() => undefined);
  }, []);

  const value = useMemo(() => ({ density, setDensity, sidebarCollapsed, setSidebarCollapsed }), [density, setDensity, sidebarCollapsed, setSidebarCollapsed]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePreferences(): Preferences {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePreferences must be used inside PreferencesProvider');
  return v;
}
